'use client';

import Link from '@/a2ui/router/link';
import { useWorkbenchPathname } from '@/a2ui/router/useWorkbenchPathname';
import { useRouter } from '@tanstack/react-router';
import { animate, useMotionValue, useSpring } from 'framer-motion';
import { Bot, LayoutDashboard, PanelLeft, PanelLeftClose, Puzzle, SquareSplitHorizontal, X } from 'lucide-react';
import { type CSSProperties, type Dispatch, type MouseEvent as ReactMouseEvent, type PointerEvent as ReactPointerEvent, type SetStateAction, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { HoverCardTooltip } from '@/components/custom/chat/left-sidebar/HoverCardTooltip';
import { Drawer, DrawerContent, DrawerDescription, DrawerOverlay, DrawerPortal, DrawerTitle } from '@/components/ui/drawer';
import { cn } from '@/lib/utils/style';
import { A2UI_GALLERY_CARD_HOVER_EVENT, A2UI_GALLERY_SIDEBAR_FOCUS_EVENT } from './a2uiGalleryEvents';
import type { A2UIGalleryCollection, A2UIGallerySidebarEntry } from './a2uiGalleryExamples';

type SidebarShortcut = { id: string; label: string; href: string; icon: typeof LayoutDashboard };
type SidebarExampleItem = Pick<A2UIGallerySidebarEntry, 'id' | 'title'>;

const SIDEBAR_STORAGE_KEY = 'a2ui-gallery-sidebar-expanded';
export const A2UI_GALLERY_SIDEBAR_COOKIE = 'a2ui_gallery_sidebar_expanded';
const SIDEBAR_ITEM_ACTIVE_CLASS = 'bg-[#EE5C2A]/10 text-[#EE5C2A]';
const SIDEBAR_ITEM_CARD_HOVER_CLASS = 'bg-[#eee8df] text-[#171411]';
const SIDEBAR_ITEM_HOVER_CLASS = 'hover:bg-black/[0.04] hover:text-[#171411]';
const SIDEBAR_ITEM_BASE_CLASS = 'rounded-sm text-sm font-medium';
const SIDEBAR_FADE_CLASS = 'a2ui-sidebar-fade-motion';
const SIDEBAR_ICON_SLOT_CLASS = 'flex h-9 w-9 shrink-0 items-center justify-center';
const SIDEBAR_PINNED_LABEL_CLASS = `min-w-0 flex-1 translate-y-[0.5px] truncate whitespace-nowrap pr-3 text-left ${SIDEBAR_FADE_CLASS}`; // The pinned entry labels need a tiny optical baseline correction against 16px Lucide glyphs; keep this on the shared label token instead of nudging individual icons or rows.
const SIDEBAR_TOOLTIP_SIDE_OFFSET = 10;
const SIDEBAR_SCROLL_FADE_SIZE = '18px';
const SIDEBAR_REVEAL_PADDING = 12;
const MOBILE_DRAWER_WIDTH_STYLE = 'min(19rem, calc(100vw - 3rem))';
const MOBILE_EDGE_SWIPE_WIDTH = 18;
const MOBILE_EDGE_SWIPE_START_DISTANCE = 6;
const MOBILE_EDGE_SWIPE_OPEN_DISTANCE = 52;
const MOBILE_EDGE_SWIPE_MAX_VERTICAL_DRIFT = 32;
const MOBILE_DRAWER_REM_WIDTH = 19;
const MOBILE_DRAWER_VIEWPORT_GAP_REM = 3;
const MOBILE_DRAWER_SHADOW_ALPHA = 0.16;
const MOBILE_DRAWER_CLOSE_MS = 260;
const getMobileDrawerShadow = (progress: number) => `16px 0 40px rgba(32,24,12,${Math.min(MOBILE_DRAWER_SHADOW_ALPHA, Math.max(0, progress) * MOBILE_DRAWER_SHADOW_ALPHA).toFixed(3)})`;
const isSafariBrowser = () => typeof navigator !== 'undefined' && /Safari\//.test(navigator.userAgent) && !/Chrome\/|Chromium\/|Edg\//.test(navigator.userAgent);
const getMobileDrawerWidth = () => {
  if (typeof window === 'undefined') return 304;
  const rootFontSize = Number.parseFloat(window.getComputedStyle(document.documentElement).fontSize) || 16;
  return Math.max(0, Math.min(MOBILE_DRAWER_REM_WIDTH * rootFontSize, window.innerWidth - MOBILE_DRAWER_VIEWPORT_GAP_REM * rootFontSize));
};
const WORKBENCH_STATIC_PRELOAD_PATHS = ['/a2ui-gallery', '/a2ui-examples', '/a2ui-preview', '/chat'] as const;
const WORKBENCH_DYNAMIC_PRELOAD_ROUTE_IDS = ['/_workbench/a2ui-gallery_/$exampleId'] as const;
const GALLERY_ROUTE = '/a2ui-gallery';
const EXAMPLES_ROUTE = '/a2ui-examples';
const WORKBENCH_SHORTCUTS: SidebarShortcut[] = [
  { id: 'chat', label: 'Chat', href: '/chat', icon: Bot },
  { id: 'gallery', label: 'Gallery', href: GALLERY_ROUTE, icon: LayoutDashboard },
  { id: 'examples', label: 'Showcases', href: EXAMPLES_ROUTE, icon: Puzzle },
  { id: 'preview', label: 'Playground', href: '/a2ui-preview', icon: SquareSplitHorizontal },
];
const getCurrentCollection = (pathname: string, examples: A2UIGallerySidebarEntry[]): A2UIGalleryCollection => {
  if (pathname.startsWith(EXAMPLES_ROUTE)) return 'examples';
  if (pathname === GALLERY_ROUTE || pathname.startsWith('/a2ui-gallery/components')) return 'components';
  return examples.find((example) => pathname === `/a2ui-gallery/${example.id}`)?.kind ?? 'components';
};
const isWorkbenchShortcutActive = (href: string, pathname: string, currentCollection: A2UIGalleryCollection) =>
  href === GALLERY_ROUTE
    ? (pathname === GALLERY_ROUTE || pathname.startsWith('/a2ui-gallery/')) && currentCollection === 'components'
    : href === EXAMPLES_ROUTE
      ? (pathname === EXAMPLES_ROUTE || pathname.startsWith('/a2ui-gallery/')) && currentCollection === 'examples'
      : pathname === href || (href === '/a2ui-preview' && pathname.startsWith('/a2ui-preview/'));
const persistSidebarExpanded = (sidebarExpanded: boolean) => {
  const persisted = sidebarExpanded ? '1' : '0';
  window.localStorage.setItem(SIDEBAR_STORAGE_KEY, persisted);
  document.cookie = `${A2UI_GALLERY_SIDEBAR_COOKIE}=${persisted}; path=/; max-age=31536000; samesite=lax`; // Persist to both localStorage and cookie: localStorage keeps the client hot path simple, while the cookie lets the server render the correct initial rail state and avoids hydration mismatches when the remembered value is collapsed.
};

function useIsNarrowViewport() {
  const [narrow, setNarrow] = useState(false); // SSR + initial render assume desktop; the matchMedia listener flips this on the client so vaul's modal listeners stay off on wide viewports where the mobile drawer is `md:hidden`. Without this gate, every desktop click is treated as `pointerDownOutside` and force-collapses the sidebar.
  useEffect(() => {
    const mq = window.matchMedia('(max-width: 767.98px)');
    const update = () => setNarrow(mq.matches);
    update();
    mq.addEventListener('change', update);
    return () => mq.removeEventListener('change', update);
  }, []);
  return narrow;
}

function useScrollFadeMask() {
  const containerRef = useRef<HTMLDivElement | null>(null);
  const topSentinelRef = useRef<HTMLDivElement | null>(null);
  const bottomSentinelRef = useRef<HTMLDivElement | null>(null);
  const [showTopFade, setShowTopFade] = useState(false);
  const [showBottomFade, setShowBottomFade] = useState(false);
  useEffect(() => {
    const root = containerRef.current;
    const top = topSentinelRef.current;
    const bottom = bottomSentinelRef.current;
    if (!root || !top || !bottom) return;
    let frame: number | null = null;
    const syncFromScrollMetrics = () => {
      frame = null;
      const canScroll = root.scrollHeight > root.clientHeight + 1;
      setShowTopFade(canScroll && root.scrollTop > 1);
      setShowBottomFade(canScroll && root.scrollTop + root.clientHeight < root.scrollHeight - 1);
    };
    const scheduleSync = () => {
      if (frame !== null) return;
      frame = window.requestAnimationFrame(syncFromScrollMetrics);
    };
    const observer =
      typeof IntersectionObserver === 'undefined'
        ? null
        : new IntersectionObserver(
            (entries) => {
              for (const entry of entries) {
                if (entry.target === top) setShowTopFade(!entry.isIntersecting);
                else if (entry.target === bottom) setShowBottomFade(!entry.isIntersecting);
              }
            },
            { root, threshold: 0 },
          );
    observer?.observe(top);
    observer?.observe(bottom);
    root.addEventListener('scroll', scheduleSync, { passive: true });
    const resizeObserver = typeof ResizeObserver === 'undefined' ? null : new ResizeObserver(scheduleSync);
    resizeObserver?.observe(root);
    const mutationObserver = typeof MutationObserver === 'undefined' ? null : new MutationObserver(scheduleSync);
    mutationObserver?.observe(root, { childList: true, subtree: true, characterData: true });
    scheduleSync();
    return () => {
      if (frame !== null) window.cancelAnimationFrame(frame);
      observer?.disconnect();
      resizeObserver?.disconnect();
      mutationObserver?.disconnect();
      root.removeEventListener('scroll', scheduleSync);
    };
  }, []);
  return { containerRef, topSentinelRef, bottomSentinelRef, showTopFade, showBottomFade };
}

const getScrollFadeMaskStyle = (showTopFade: boolean, showBottomFade: boolean): CSSProperties | undefined => {
  if (!showTopFade && !showBottomFade) return undefined;
  const maskImage = `linear-gradient(to bottom, ${showTopFade ? `transparent 0, #000 ${SIDEBAR_SCROLL_FADE_SIZE}` : '#000 0'}, ${showBottomFade ? `#000 calc(100% - ${SIDEBAR_SCROLL_FADE_SIZE}), transparent 100%` : '#000 100%'})`;
  return { WebkitMaskImage: maskImage, maskImage };
};

export function useA2UIWorkbenchSidebarExpanded(initialSidebarExpanded: boolean) {
  const [sidebarExpanded, setSidebarExpanded] = useState(initialSidebarExpanded);

  useEffect(() => {
    persistSidebarExpanded(sidebarExpanded);
  }, [sidebarExpanded]);

  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.repeat || event.altKey || event.shiftKey || !(event.ctrlKey || event.metaKey) || (event.key.toLowerCase() !== 'b' && event.code !== 'KeyB')) return;
      event.preventDefault();
      setSidebarExpanded((current) => !current);
    };
    window.addEventListener('keydown', onKeyDown, { capture: true });
    return () => window.removeEventListener('keydown', onKeyDown, { capture: true });
  }, []);

  return [sidebarExpanded, setSidebarExpanded] as const;
}

export default function A2UIWorkbenchSidebar({ examples, sidebarExpanded, setSidebarExpanded }: { examples: A2UIGallerySidebarEntry[]; sidebarExpanded: boolean; setSidebarExpanded: Dispatch<SetStateAction<boolean>> }) {
  const router = useRouter();
  const pathname = useWorkbenchPathname();
  const [hoveredExampleId, setHoveredExampleId] = useState<string | null>(null);
  const { containerRef: exampleListRef, topSentinelRef, bottomSentinelRef, showTopFade: showListTopFade, showBottomFade: showListBottomFade } = useScrollFadeMask();
  const previewScrollIntent = useMotionValue(0);
  const previewScrollAnimationRef = useRef<{ stop: () => void } | null>(null);
  const previewScrollWriteRef = useRef(false);
  const previewScrollTop = useSpring(previewScrollIntent, { stiffness: 240, damping: 34, mass: 0.42, restDelta: 0.5, restSpeed: 8 });
  const currentCollection = useMemo(() => getCurrentCollection(pathname, examples), [examples, pathname]);
  useEffect(() => {
    const preload = () => {
      for (const path of WORKBENCH_STATIC_PRELOAD_PATHS) {
        void router.preloadRoute({ to: path }); // Warm both the route component chunk AND the loader's data, so even routes the user never hovered are click-instant. Combined with `defaultStaleTime: Infinity`, each loader runs once per session at most.
      }
      for (const routeId of WORKBENCH_DYNAMIC_PRELOAD_ROUTE_IDS) {
        const route = router.looseRoutesById[routeId];
        if (route) void router.loadRouteChunk(route, ['component']); // Code-only for dynamic routes since we don't know the param value up front; the data preload happens on hover/click of a specific example.
      }
    };
    if (typeof window.requestIdleCallback === 'function') {
      const id = window.requestIdleCallback(preload, { timeout: 1200 });
      return () => window.cancelIdleCallback(id);
    }
    const id = window.setTimeout(preload, 0);
    return () => window.clearTimeout(id);
  }, [router]);
  const exampleItems = useMemo<SidebarExampleItem[]>(() => examples.filter((example) => example.kind === currentCollection).map(({ id, title }) => ({ id, title })), [currentCollection, examples]);
  const emitSidebarFocusState = (hovered: boolean, exampleId: string | null) => window.dispatchEvent(new CustomEvent(A2UI_GALLERY_SIDEBAR_FOCUS_EVENT, { detail: { hovered, exampleId } }));
  const syncSidebarPreviewCardFromTarget = useCallback(
    (target: EventTarget | null) => {
      if ((pathname !== GALLERY_ROUTE && pathname !== EXAMPLES_ROUTE) || !(target instanceof Element)) return;
      const exampleId = target.closest<HTMLElement>('[data-a2ui-gallery-sidebar-example-id]')?.dataset.a2uiGallerySidebarExampleId;
      if (!exampleId) return;
      const card = document.querySelector<HTMLElement>(`[data-a2ui-gallery-card-id="${exampleId}"]`);
      const scroller = document.querySelector<HTMLElement>('[data-a2ui-gallery-scroll-region]');
      if (!card || !scroller) return;
      const padding = 40;
      const cardRect = card.getBoundingClientRect();
      const scrollerRect = scroller.getBoundingClientRect();
      if (cardRect.top >= scrollerRect.top + padding && cardRect.bottom <= scrollerRect.bottom - padding) return; // This 40px band is intentionally a scroll-only safety margin, not the same thing as the page's permanent padding. The user wanted hovered cards to stop farther from the viewport edge without making the whole gallery feel more padded all the time, so keep this threshold decoupled from the static wall spacing.
      const rawNextScrollTop = scroller.scrollTop + (cardRect.top < scrollerRect.top + padding ? cardRect.top - scrollerRect.top - padding : cardRect.bottom - scrollerRect.bottom + padding);
      const nextScrollTop = Math.max(0, Math.min(scroller.scrollHeight - scroller.clientHeight, rawNextScrollTop)); // Clamp to the real scrollable range so hovering the first/last card never leaves the spring chasing an unreachable negative/overflow target and then "snapping" to a stop at the boundary.
      if (isSafariBrowser()) {
        scroller.scrollTo({ top: nextScrollTop, behavior: 'smooth' }); // Safari still drops frames more readily under the rAF-driven spring path, so keep its hover scroll on the browser-native compositor animation and only use the spring retargeting path elsewhere.
        return;
      }
      const currentScrollTop = scroller.scrollTop;
      const distance = Math.abs(nextScrollTop - currentScrollTop);
      previewScrollAnimationRef.current = animate(previewScrollIntent, nextScrollTop, { type: 'tween', duration: Math.min(0.54, Math.max(0.2, distance / 2000)), ease: [0.24, 0.04, 0.22, 1] }); // Motion's tween gives the user-requested slow-fast-slow velocity curve much more predictably than a raw physics spring. We still route it through one shared motion value so every retarget starts from the live in-flight position, while the derived spring below smooths the handoff enough that rapid hover changes do not feel like disconnected scroll runs.
    },
    [pathname, previewScrollIntent],
  );
  const syncSidebarListItemFromHoveredExample = useCallback(
    (exampleId: string | null) => {
      if (!exampleId) return;
      const list = exampleListRef.current;
      if (!list) return;
      const item = Array.from(list.querySelectorAll<HTMLElement>('[data-a2ui-gallery-sidebar-example-id]')).find((node) => node.dataset.a2uiGallerySidebarExampleId === exampleId);
      if (!item) return;
      const listRect = list.getBoundingClientRect();
      const itemRect = item.getBoundingClientRect();
      if (itemRect.top >= listRect.top + SIDEBAR_REVEAL_PADDING && itemRect.bottom <= listRect.bottom - SIDEBAR_REVEAL_PADDING) return;
      const rawNextScrollTop = list.scrollTop + (itemRect.top < listRect.top + SIDEBAR_REVEAL_PADDING ? itemRect.top - listRect.top - SIDEBAR_REVEAL_PADDING : itemRect.bottom - listRect.bottom + SIDEBAR_REVEAL_PADDING);
      const nextScrollTop = Math.max(0, Math.min(list.scrollHeight - list.clientHeight, rawNextScrollTop));
      list.scrollTo({ top: nextScrollTop, behavior: 'smooth' });
    },
    [exampleListRef],
  );

  useEffect(() => {
    const handleHoveredExample = (event: Event) => {
      const exampleId = (event as CustomEvent<{ exampleId: string | null }>).detail?.exampleId ?? null;
      setHoveredExampleId(exampleId); // Keep the bridge one-way and local: the sidebar only mirrors the currently hovered gallery card. It does not try to own hover state itself, which avoids feedback loops and the earlier "looks correct in React, but real browser hover over nested preview DOM never reaches the outer card" failure mode.
      syncSidebarListItemFromHoveredExample(exampleId);
    };
    window.addEventListener(A2UI_GALLERY_CARD_HOVER_EVENT, handleHoveredExample as EventListener);
    return () => window.removeEventListener(A2UI_GALLERY_CARD_HOVER_EVENT, handleHoveredExample as EventListener);
  }, [syncSidebarListItemFromHoveredExample]);

  useEffect(() => {
    const scroller = document.querySelector<HTMLElement>('[data-a2ui-gallery-scroll-region]');
    if (!scroller) return;
    previewScrollIntent.jump(scroller.scrollTop);
    const unsubscribe = previewScrollTop.on('change', (latest) => {
      if (Math.abs(scroller.scrollTop - latest) < 0.5) return;
      previewScrollWriteRef.current = true;
      scroller.scrollTop = latest;
      requestAnimationFrame(() => {
        previewScrollWriteRef.current = false;
      });
    });
    const handleNativeScroll = () => {
      if (previewScrollWriteRef.current) return;
      previewScrollAnimationRef.current?.stop();
      previewScrollTop.jump(scroller.scrollTop);
      previewScrollIntent.jump(scroller.scrollTop); // Keep Motion's source of truth aligned with manual wheel/trackpad scrolling so the next hover tween starts from the real current position. Motion-authored writes are ignored via `previewScrollWriteRef`, so hover-to-hover retargeting still stays continuous.
    };
    scroller.addEventListener('scroll', handleNativeScroll, { passive: true });
    return () => {
      scroller.removeEventListener('scroll', handleNativeScroll);
      previewScrollAnimationRef.current?.stop();
      unsubscribe();
    };
  }, [previewScrollIntent, previewScrollTop]);

  useEffect(() => {
    const list = exampleListRef.current;
    if (!list) return;
    const handlePointerLikeEvent = (event: Event) => syncSidebarPreviewCardFromTarget(event.target); // Keep this listener native and delegated from the scrolling nav itself. React capture handlers looked fine in code but failed intermittently against the real Link subtree in the browser, so the durable fix is to let the DOM's own `mouseover` / `focusin` bubbling drive the scroll sync.
    const handleSidebarFocusEvent = (event: Event) => {
      const target = event.target instanceof Element ? event.target : null;
      const exampleId = target?.closest<HTMLElement>('[data-a2ui-gallery-sidebar-example-id]')?.dataset.a2uiGallerySidebarExampleId ?? null;
      emitSidebarFocusState(Boolean(exampleId), exampleId);
    };
    const handleSidebarMouseLeave = () => emitSidebarFocusState(false, null);
    const handleSidebarFocusOut = (event: FocusEvent) => {
      const nextTarget = event.relatedTarget;
      if (nextTarget instanceof Element && list.contains(nextTarget)) {
        const exampleId = nextTarget.closest<HTMLElement>('[data-a2ui-gallery-sidebar-example-id]')?.dataset.a2uiGallerySidebarExampleId ?? null;
        emitSidebarFocusState(Boolean(exampleId), exampleId);
        return;
      }
      emitSidebarFocusState(false, null);
    };
    list.addEventListener('mouseover', handlePointerLikeEvent);
    list.addEventListener('focusin', handlePointerLikeEvent);
    list.addEventListener('mouseover', handleSidebarFocusEvent);
    list.addEventListener('focusin', handleSidebarFocusEvent);
    list.addEventListener('mouseleave', handleSidebarMouseLeave);
    list.addEventListener('focusout', handleSidebarFocusOut);
    return () => {
      list.removeEventListener('mouseover', handlePointerLikeEvent);
      list.removeEventListener('focusin', handlePointerLikeEvent);
      list.removeEventListener('mouseover', handleSidebarFocusEvent);
      list.removeEventListener('focusin', handleSidebarFocusEvent);
      list.removeEventListener('mouseleave', handleSidebarMouseLeave);
      list.removeEventListener('focusout', handleSidebarFocusOut);
      emitSidebarFocusState(false, null);
    };
  }, [exampleListRef, pathname, sidebarExpanded, syncSidebarPreviewCardFromTarget]);

  return (
    <aside className="a2ui-workbench-sidebar pointer-events-none fixed inset-y-0 left-[2px] z-[220] w-[var(--demo-sidebar)] p-1.5">
      {' '}
      {/* The whole debug sidebar intentionally sits 2px off the viewport edge so the collapsed toggle can keep equal-looking outer breathing room without needing per-button fudge factors. If this offset is removed, the header toggle may look mathematically aligned but still read visually too far left. */}
      <div
        className={cn('a2ui-sidebar-width-motion pointer-events-auto relative z-[100] flex h-full shrink-0 flex-col overflow-x-hidden overflow-y-hidden pb-2 pt-1', sidebarExpanded ? 'w-[var(--demo-sidebar)]' : 'w-[var(--demo-rail)]')}
      >
        <SidebarHeader sidebarExpanded={sidebarExpanded} setSidebarExpanded={setSidebarExpanded} />
        <div className="mt-3 flex min-h-0 flex-1 flex-col px-2 pb-1">
          <nav className="space-y-1">
            {WORKBENCH_SHORTCUTS.map(({ id, label, href, icon: Icon }) => {
              const active = isWorkbenchShortcutActive(href, pathname, currentCollection);
              return <SidebarPinnedLink key={id} href={href} label={label} icon={Icon} active={active} sidebarExpanded={sidebarExpanded} />;
            })}
          </nav>
          {isWorkbenchShortcutActive(GALLERY_ROUTE, pathname, currentCollection) || isWorkbenchShortcutActive(EXAMPLES_ROUTE, pathname, currentCollection) ? (
            <div className={cn('mt-4 flex min-h-0 flex-1 flex-col overflow-hidden', SIDEBAR_FADE_CLASS, sidebarExpanded ? 'opacity-100' : 'pointer-events-none opacity-0')}>
              <div className="px-3 text-[11px] font-medium uppercase tracking-[0.18em] text-[#9a8f84]">{currentCollection === 'examples' ? 'showcases' : 'components'}</div>
              <div className="relative mt-2 min-h-0 flex-1 overflow-hidden">
                <div ref={exampleListRef} className="h-full overflow-y-auto pr-1 no-scrollbar" style={getScrollFadeMaskStyle(showListTopFade, showListBottomFade)}>
                  <div ref={topSentinelRef} aria-hidden="true" className="h-px" />
                  <nav className="space-y-1">
                    {exampleItems.map(({ id, title }) => {
                      const href = `/a2ui-gallery/${id}`;
                      return <SidebarTextLink key={id} exampleId={id} href={href} label={title} active={pathname === href} hovered={(pathname === GALLERY_ROUTE || pathname === EXAMPLES_ROUTE) && hoveredExampleId === id} />;
                    })}
                  </nav>
                  <div ref={bottomSentinelRef} aria-hidden="true" className="h-px" />
                </div>
              </div>
            </div>
          ) : null}
        </div>
      </div>
    </aside>
  );
}

export function MobileA2UIWorkbenchDrawer({ examples, sidebarExpanded, setSidebarExpanded }: { examples: A2UIGallerySidebarEntry[]; sidebarExpanded: boolean; setSidebarExpanded: Dispatch<SetStateAction<boolean>> }) {
  const router = useRouter();
  const pathname = useWorkbenchPathname();
  const isNarrow = useIsNarrowViewport();
  const [open, setDrawerOpen] = useState(sidebarExpanded);
  const [openInitialTransform, setOpenInitialTransform] = useState('100%');
  const [openOverlayMode, setOpenOverlayMode] = useState<'normal' | 'hidden' | 'solid' | 'closed'>('normal');
  const edgeSwipeRef = useRef<{ pointerId: number; x: number; y: number; drawerWidth: number; offset: number; previewing: boolean } | null>(null);
  const previewDrawerRef = useRef<HTMLDivElement | null>(null);
  const previewOverlayRef = useRef<HTMLDivElement | null>(null);
  const previewHideTimeoutRef = useRef<number | null>(null);
  const previewOpenCleanupTimeoutRef = useRef<number | null>(null);
  const closeAnimationTimeoutRef = useRef<number | null>(null);
  const committingCloseRef = useRef(false);
  const currentCollection = useMemo(() => getCurrentCollection(pathname, examples), [examples, pathname]);
  const exampleItems = useMemo<SidebarExampleItem[]>(() => examples.filter((example) => example.kind === currentCollection).map(({ id, title }) => ({ id, title })), [currentCollection, examples]);
  const navigateFromDrawer = useCallback(
    (event: ReactMouseEvent<HTMLAnchorElement>, href: string) => {
      event.preventDefault();
      void router.navigate({ to: href });
      setSidebarExpanded(false);
    },
    [router, setSidebarExpanded],
  );
  // Vaul can only drag after Dialog content is mounted, so the closed-edge pull uses a DOM-written preview layer to avoid mount-time flashes and per-frame React renders.
  const drawEdgePreview = useCallback((offset: number, drawerWidth: number) => {
    const drawer = previewDrawerRef.current;
    const overlay = previewOverlayRef.current;
    if (!drawer || !overlay) return;
    const progress = Math.min(1, offset / Math.max(drawerWidth, 1));
    if (previewHideTimeoutRef.current !== null) window.clearTimeout(previewHideTimeoutRef.current);
    drawer.style.visibility = 'visible';
    drawer.style.transition = 'none';
    drawer.style.transform = `translate3d(${Math.round(offset - drawerWidth)}px, 0, 0)`;
    drawer.style.boxShadow = getMobileDrawerShadow(progress);
    overlay.style.visibility = 'visible';
    overlay.style.transition = 'none';
    overlay.style.opacity = `${progress}`;
  }, []);
  const hideEdgePreview = useCallback((drawerWidth: number, animateClose: boolean) => {
    const drawer = previewDrawerRef.current;
    const overlay = previewOverlayRef.current;
    if (!drawer || !overlay) return;
    if (previewHideTimeoutRef.current !== null) window.clearTimeout(previewHideTimeoutRef.current);
    drawer.style.transition = animateClose ? 'transform 180ms cubic-bezier(0.32,0.72,0,1), box-shadow 180ms cubic-bezier(0.32,0.72,0,1)' : 'none';
    drawer.style.transform = `translate3d(${-drawerWidth}px, 0, 0)`;
    drawer.style.boxShadow = getMobileDrawerShadow(0);
    overlay.style.transition = animateClose ? 'opacity 180ms cubic-bezier(0.32,0.72,0,1)' : 'none';
    overlay.style.opacity = '0';
    previewHideTimeoutRef.current = window.setTimeout(
      () => {
        drawer.style.visibility = 'hidden';
        overlay.style.visibility = 'hidden';
      },
      animateClose ? 190 : 0,
    );
  }, []);
  const transitionPreviewOverlayToOpen = useCallback(() => {
    const overlay = previewOverlayRef.current;
    if (!overlay) return;
    overlay.style.visibility = 'visible';
    overlay.style.transition = 'opacity 500ms linear';
    overlay.style.opacity = '1';
  }, []);
  const finishOpenPreviewHandoff = useCallback(() => {
    if (previewOpenCleanupTimeoutRef.current !== null) {
      window.clearTimeout(previewOpenCleanupTimeoutRef.current);
      previewOpenCleanupTimeoutRef.current = null;
    }
    const overlay = document.querySelector<HTMLElement>('[data-vaul-overlay]');
    if (overlay) {
      overlay.style.animation = 'none';
      overlay.style.opacity = '1';
    }
    const drawer = document.querySelector<HTMLElement>('[data-vaul-drawer]');
    if (drawer) {
      drawer.style.animation = 'none';
      drawer.style.transition = 'none';
      drawer.style.transform = 'translate3d(0, 0, 0)';
    }
    setOpenOverlayMode('solid');
    setOpenInitialTransform('100%');
    hideEdgePreview(getMobileDrawerWidth(), false);
  }, [hideEdgePreview]);
  const clearOpenPreviewHandoff = useCallback(() => {
    if (previewOpenCleanupTimeoutRef.current === null) return;
    window.clearTimeout(previewOpenCleanupTimeoutRef.current);
    previewOpenCleanupTimeoutRef.current = null;
  }, []);
  const startDrawerOpenFromCurrentFrame = useCallback(() => {
    if (closeAnimationTimeoutRef.current !== null) {
      window.clearTimeout(closeAnimationTimeoutRef.current);
      closeAnimationTimeoutRef.current = null;
    }
    clearOpenPreviewHandoff();
    const drawer = document.querySelector<HTMLElement>('[data-vaul-drawer]');
    const overlay = document.querySelector<HTMLElement>('[data-vaul-overlay]');
    if (!drawer || !overlay) {
      setOpenOverlayMode('normal');
      setDrawerOpen(true);
      return;
    }
    const currentDrawerTransform = window.getComputedStyle(drawer).transform;
    const currentOverlayOpacity = window.getComputedStyle(overlay).opacity;
    overlay.style.animation = 'none';
    overlay.style.transition = 'none';
    overlay.style.opacity = currentOverlayOpacity;
    drawer.style.animation = 'none';
    drawer.style.transition = 'none';
    drawer.style.transform = currentDrawerTransform === 'none' ? 'translate3d(0, 0, 0)' : currentDrawerTransform;
    void drawer.offsetHeight; // Match the close-interrupt path: freeze the current close frame before retargeting, so rapid Cmd/Ctrl+B reverses direction instead of leaving stale close styles on an open drawer.
    overlay.style.transition = `opacity ${MOBILE_DRAWER_CLOSE_MS}ms linear`;
    overlay.style.opacity = '1';
    drawer.style.transition = `transform ${MOBILE_DRAWER_CLOSE_MS}ms cubic-bezier(0.32,0.72,0,1)`;
    drawer.style.transform = 'translate3d(0, 0, 0)';
    setOpenOverlayMode('solid');
    previewOpenCleanupTimeoutRef.current = window.setTimeout(finishOpenPreviewHandoff, MOBILE_DRAWER_CLOSE_MS + 20);
  }, [clearOpenPreviewHandoff, finishOpenPreviewHandoff]);
  const closeEdgeDrag = useCallback(
    (target: HTMLDivElement, pointerId: number, animatePreview: boolean) => {
      const edgeSwipe = edgeSwipeRef.current;
      if (target.hasPointerCapture(pointerId)) target.releasePointerCapture(pointerId);
      edgeSwipeRef.current = null;
      hideEdgePreview(edgeSwipe?.drawerWidth ?? getMobileDrawerWidth(), animatePreview);
    },
    [hideEdgePreview],
  );
  const releaseEdgeDragForOpen = useCallback((target: HTMLDivElement, pointerId: number) => {
    if (target.hasPointerCapture(pointerId)) target.releasePointerCapture(pointerId);
    edgeSwipeRef.current = null;
  }, []);
  const commitDrawerClose = useCallback(() => {
    if (closeAnimationTimeoutRef.current !== null) {
      window.clearTimeout(closeAnimationTimeoutRef.current);
      closeAnimationTimeoutRef.current = null;
    }
    committingCloseRef.current = true;
    setOpenInitialTransform('100%');
    setOpenOverlayMode('closed');
    setDrawerOpen(false);
    window.requestAnimationFrame(() => {
      committingCloseRef.current = false;
    });
  }, []);
  const startDrawerClose = useCallback(
    (syncSidebarState = true) => {
      if (closeAnimationTimeoutRef.current !== null) return;
      clearOpenPreviewHandoff();
      if (syncSidebarState && sidebarExpanded) setSidebarExpanded(false);
      const drawer = document.querySelector<HTMLElement>('[data-vaul-drawer]');
      const overlay = document.querySelector<HTMLElement>('[data-vaul-overlay]');
      if (!drawer || !overlay) {
        commitDrawerClose();
        return;
      }
      hideEdgePreview(getMobileDrawerWidth(), true);
      const currentDrawerTransform = window.getComputedStyle(drawer).transform;
      const currentOverlayOpacity = window.getComputedStyle(overlay).opacity;
      overlay.style.animation = 'none';
      overlay.style.transition = 'none';
      overlay.style.opacity = currentOverlayOpacity;
      drawer.style.animation = 'none';
      drawer.style.transition = 'none';
      drawer.style.transform = currentDrawerTransform === 'none' ? 'translate3d(0, 0, 0)' : currentDrawerTransform;
      void drawer.offsetHeight; // Freeze the in-flight opening frame before retargeting; otherwise canceling the CSS keyframe can snap to either fully open or fully closed.
      overlay.style.transition = `opacity ${MOBILE_DRAWER_CLOSE_MS}ms linear`;
      overlay.style.opacity = '0';
      drawer.style.transition = `transform ${MOBILE_DRAWER_CLOSE_MS}ms cubic-bezier(0.32,0.72,0,1)`;
      drawer.style.transform = 'translate3d(-100%, 0, 0)';
      closeAnimationTimeoutRef.current = window.setTimeout(commitDrawerClose, MOBILE_DRAWER_CLOSE_MS + 20);
    },
    [clearOpenPreviewHandoff, commitDrawerClose, hideEdgePreview, setSidebarExpanded, sidebarExpanded],
  );
  const handleDrawerOpenChange = useCallback(
    (nextOpen: boolean) => {
      if (!nextOpen) {
        if (committingCloseRef.current || !open) {
          setOpenInitialTransform('100%');
          setOpenOverlayMode('normal');
          setDrawerOpen(false);
          return;
        }
        startDrawerClose();
        return;
      }
      setSidebarExpanded(true);
    },
    [open, setSidebarExpanded, startDrawerClose],
  );
  const handleDrawerAnimationEnd = useCallback(
    (nextOpen: boolean) => {
      if (!nextOpen || !sidebarExpanded || closeAnimationTimeoutRef.current !== null) return;
      finishOpenPreviewHandoff();
    },
    [finishOpenPreviewHandoff, sidebarExpanded],
  );

  useEffect(() => {
    if (sidebarExpanded) {
      if (closeAnimationTimeoutRef.current !== null) {
        startDrawerOpenFromCurrentFrame();
        return;
      }
      if (openOverlayMode === 'closed') setOpenOverlayMode('normal');
      if (!open) setDrawerOpen(true);
      return;
    }
    if (open) startDrawerClose(false);
  }, [open, openOverlayMode, sidebarExpanded, startDrawerClose, startDrawerOpenFromCurrentFrame]);

  useEffect(() => {
    if (closeAnimationTimeoutRef.current !== null) return; // Drawer-link click already kicked off the animated close above; do not yank the in-flight transition out from under it on the same render.
    setDrawerOpen(false);
    setOpenInitialTransform('100%');
    setOpenOverlayMode('normal');
    hideEdgePreview(getMobileDrawerWidth(), false);
  }, [hideEdgePreview, pathname]);

  useEffect(() => {
    if (!open || openOverlayMode !== 'hidden') return;
    if (previewOpenCleanupTimeoutRef.current !== null) window.clearTimeout(previewOpenCleanupTimeoutRef.current);
    previewOpenCleanupTimeoutRef.current = window.setTimeout(finishOpenPreviewHandoff, 520);
    return () => {
      if (previewOpenCleanupTimeoutRef.current !== null) window.clearTimeout(previewOpenCleanupTimeoutRef.current);
    };
  }, [finishOpenPreviewHandoff, open, openOverlayMode]);

  useEffect(
    () => () => {
      if (previewHideTimeoutRef.current !== null) window.clearTimeout(previewHideTimeoutRef.current);
      if (previewOpenCleanupTimeoutRef.current !== null) window.clearTimeout(previewOpenCleanupTimeoutRef.current);
      if (closeAnimationTimeoutRef.current !== null) window.clearTimeout(closeAnimationTimeoutRef.current);
    },
    [],
  );

  const handleEdgePointerDown = (event: ReactPointerEvent<HTMLDivElement>) => {
    if (open || (event.pointerType !== 'mouse' && !event.isPrimary)) return;
    const drawerWidth = getMobileDrawerWidth();
    edgeSwipeRef.current = { pointerId: event.pointerId, x: event.clientX, y: event.clientY, drawerWidth, offset: 0, previewing: false };
    event.currentTarget.setPointerCapture(event.pointerId);
  };
  const handleEdgePointerMove = (event: ReactPointerEvent<HTMLDivElement>) => {
    const edgeSwipe = edgeSwipeRef.current;
    if (!edgeSwipe || edgeSwipe.pointerId !== event.pointerId) return;
    const deltaX = event.clientX - edgeSwipe.x;
    const deltaY = event.clientY - edgeSwipe.y;
    if (Math.abs(deltaY) > MOBILE_EDGE_SWIPE_MAX_VERTICAL_DRIFT && Math.abs(deltaY) > deltaX) {
      closeEdgeDrag(event.currentTarget, event.pointerId, false);
      return;
    }
    if (Math.abs(deltaY) > MOBILE_EDGE_SWIPE_MAX_VERTICAL_DRIFT) return;
    if (deltaX < MOBILE_EDGE_SWIPE_START_DISTANCE && !edgeSwipe.previewing) return;
    edgeSwipe.previewing = true;
    edgeSwipe.offset = Math.min(Math.max(deltaX, 0), edgeSwipe.drawerWidth);
    drawEdgePreview(edgeSwipe.offset, edgeSwipe.drawerWidth);
  };
  const handleEdgePointerEnd = (event: ReactPointerEvent<HTMLDivElement>) => {
    const edgeSwipe = edgeSwipeRef.current;
    if (!edgeSwipe || edgeSwipe.pointerId !== event.pointerId) return;
    const shouldOpen = edgeSwipe.offset >= MOBILE_EDGE_SWIPE_OPEN_DISTANCE;
    if (shouldOpen) {
      setOpenInitialTransform(`${Math.max(0, ((edgeSwipe.drawerWidth - edgeSwipe.offset) / Math.max(edgeSwipe.drawerWidth, 1)) * 100)}%`);
      setOpenOverlayMode('hidden');
      transitionPreviewOverlayToOpen();
      releaseEdgeDragForOpen(event.currentTarget, event.pointerId);
    } else closeEdgeDrag(event.currentTarget, event.pointerId, true);
    if (shouldOpen) setSidebarExpanded(true);
  };
  const handleEdgePointerCancel = (event: ReactPointerEvent<HTMLDivElement>) => {
    if (edgeSwipeRef.current?.pointerId === event.pointerId) closeEdgeDrag(event.currentTarget, event.pointerId, false);
  };
  const drawerContentStyle = { width: MOBILE_DRAWER_WIDTH_STYLE, '--initial-transform': openInitialTransform } as CSSProperties;
  const drawerOverlayStyle =
    openOverlayMode === 'hidden'
      ? { animation: 'none', opacity: 0 }
      : openOverlayMode === 'solid'
        ? { animation: 'none', opacity: 1 }
        : openOverlayMode === 'closed'
          ? { animation: 'none', opacity: 0, pointerEvents: 'none' as const }
          : undefined;

  return (
    <Drawer open={isNarrow && open} onOpenChange={handleDrawerOpenChange} onAnimationEnd={handleDrawerAnimationEnd} direction="left" shouldScaleBackground={false} dismissible={true} modal={true}>
      <div
        data-a2ui-mobile-edge-swipe=""
        aria-hidden="true"
        className="fixed inset-y-0 left-0 z-[240] md:hidden"
        style={{ width: MOBILE_EDGE_SWIPE_WIDTH, touchAction: 'pan-y' }}
        onPointerDown={handleEdgePointerDown}
        onPointerMove={handleEdgePointerMove}
        onPointerUp={handleEdgePointerEnd}
        onPointerCancel={handleEdgePointerCancel}
      />
      <div ref={previewOverlayRef} aria-hidden="true" className="pointer-events-none fixed inset-0 z-[270] bg-[#171411]/35 backdrop-blur-[1px] md:hidden" style={{ opacity: 0, visibility: 'hidden' }} />
      <div
        ref={previewDrawerRef}
        inert={true}
        aria-hidden="true"
        className="pointer-events-none fixed inset-y-0 left-0 z-[275] flex h-[100dvh] flex-col overflow-hidden border-r border-[#e7ddd2] bg-[#f5f2eb] text-[#171411] md:hidden"
        style={{ width: MOBILE_DRAWER_WIDTH_STYLE, transform: `translate3d(${-getMobileDrawerWidth()}px, 0, 0)`, visibility: 'hidden', willChange: 'transform, box-shadow', boxShadow: getMobileDrawerShadow(0) }}
      >
        <MobileDrawerBody currentCollection={currentCollection} exampleItems={exampleItems} pathname={pathname} onNavigate={navigateFromDrawer} onRequestClose={startDrawerClose} />
      </div>
      <DrawerPortal>
        <DrawerOverlay
          className="md:hidden"
          style={drawerOverlayStyle}
          onPointerDownCapture={(event) => {
            event.preventDefault();
            event.stopPropagation();
            startDrawerClose();
          }}
          onPointerUpCapture={(event) => {
            event.preventDefault();
            event.stopPropagation();
          }}
          onClickCapture={(event) => {
            event.preventDefault();
            event.stopPropagation();
          }}
        />
        <DrawerContent
          className="inset-y-0 left-0 h-[100dvh] flex-col overflow-hidden border-r border-[#e7ddd2] shadow-[16px_0_40px_rgba(32,24,12,0.16)] md:hidden"
          style={drawerContentStyle}
          onEscapeKeyDown={(event) => {
            event.preventDefault();
            startDrawerClose();
          }}
          onPointerDownOutside={(event) => {
            event.preventDefault();
            startDrawerClose();
          }}
        >
          <MobileDrawerBody currentCollection={currentCollection} exampleItems={exampleItems} pathname={pathname} onNavigate={navigateFromDrawer} onRequestClose={startDrawerClose} />
        </DrawerContent>
      </DrawerPortal>
    </Drawer>
  );
}

function SidebarHeader({ sidebarExpanded, setSidebarExpanded }: { sidebarExpanded: boolean; setSidebarExpanded: Dispatch<SetStateAction<boolean>> }) {
  const [suppressCollapsedToggleTooltip, setSuppressCollapsedToggleTooltip] = useState(false);
  const handleToggleClick = () => {
    setSuppressCollapsedToggleTooltip(true); // Match `/chat` UX: after a click commits the sidebar state change, the tooltip should not immediately re-open under the pointer during the collapse animation; wait until the pointer leaves the rail cell before allowing hover again.
    setSidebarExpanded((current) => !current);
  };
  return (
    <div className="relative flex h-12 items-center">
      <div className={cn('flex min-w-0 items-center gap-2 pl-5 pr-12', SIDEBAR_FADE_CLASS, sidebarExpanded ? 'opacity-100' : 'pointer-events-none opacity-0')}>
        <p className="max-w-[8rem] truncate text-sm font-semibold text-[#171411]">A2UI Workbench</p>
      </div>
      <div
        className={cn(
          'a2ui-sidebar-toggle-motion absolute left-2 top-1/2 flex shrink-0 -translate-y-1/2 items-center will-change-transform',
          sidebarExpanded ? 'translate-x-[calc(var(--demo-sidebar)-var(--demo-rail)+2px)]' : 'translate-x-0',
        )}
      >
        {' '}
        {/* Once the entire sidebar is offset 2px from the viewport, the toggle can go back to the clean `left-2 / top-1/2` anchor. Keep this anchor and the sidebar's outer 2px offset in sync; changing only one of them reintroduces the "top/left inset should match" regression that was just corrected. */}
        <HoverCardTooltip text={suppressCollapsedToggleTooltip ? '' : sidebarExpanded ? 'Collapse sidebar' : 'Expand sidebar'} sideOffset={SIDEBAR_TOOLTIP_SIDE_OFFSET} contentClassName="z-[320]" portalled={true}>
          <div className="flex w-9 justify-center" onMouseLeave={() => setSuppressCollapsedToggleTooltip(false)} onMouseMove={() => suppressCollapsedToggleTooltip && setSuppressCollapsedToggleTooltip(false)}>
            {' '}
            {/* Keep one stable tooltip wrapper for both expanded and collapsed states. The previous branch only wrapped the collapsed button, which is why the expanded-state toggle had no tooltip at all; keeping the outer box mounted also avoids Radix `asChild` churn during the width animation. We also clear the post-click suppression on the next real pointer move, not only on full mouse-leave, because the expanding/collapsing button can slide under the cursor and otherwise feel "stuck with no tooltip" even though the user is clearly hovering intentionally again. */}
            <button
              type="button"
              aria-label={sidebarExpanded ? 'Collapse sidebar' : 'Expand sidebar'}
              onClick={handleToggleClick}
              className={cn('flex h-9 w-9 cursor-ew-resize items-center justify-center bg-transparent text-[#6F655B] appearance-none', SIDEBAR_ITEM_BASE_CLASS, SIDEBAR_ITEM_HOVER_CLASS)}
            >
              {' '}
              {/* `macaron-genui-demo` uses Tailwind v4's `size-9`; this app is on Tailwind v3, so we must spell the box as `h-9 w-9` or the toggle collapses to the 16px icon itself and appears permanently left-shifted. `aria-label` keeps the control accessible without triggering the browser's native `title` tooltip on top of our custom hover card. */}
              {sidebarExpanded ? <PanelLeftClose size={16} strokeWidth={2} className="block" /> : <PanelLeft size={16} strokeWidth={2} className="block" />}
            </button>
          </div>
        </HoverCardTooltip>
      </div>
    </div>
  );
}

function SidebarPinnedLink({ href, label, icon: Icon, active, sidebarExpanded }: { href: string; label: string; icon: typeof LayoutDashboard; active: boolean; sidebarExpanded: boolean }) {
  const [suppressTooltip, setSuppressTooltip] = useState(false);
  const handlePinnedClickCapture = (_event: ReactMouseEvent<HTMLDivElement>) => {
    setSuppressTooltip(true); // Keep the same tree for fade transitions, but locally mute the collapsed hover-card after activation so navigation/collapse does not instantly re-trigger a tooltip under the cursor.
  };
  return (
    <HoverCardTooltip text={sidebarExpanded || suppressTooltip ? '' : label} sideOffset={SIDEBAR_TOOLTIP_SIDE_OFFSET} contentClassName="z-[320]" portalled={true}>
      <div className="block w-full" onClickCapture={handlePinnedClickCapture} onMouseLeave={() => setSuppressTooltip(false)}>
        {' '}
        {/* Keep one stable wrapper in both expanded and collapsed states. The earlier conditional `row` vs `tooltip(row)` split fixed the Radix crash but accidentally remounted the link subtree, which is why `Gallery` / `Playground` stopped fading and instead popped in/out. The local click-suppression flag restores the `/chat` feel by preventing a fresh tooltip from flying in again until the pointer fully leaves the row. */}
        <Link
          href={href}
          aria-current={active ? 'page' : undefined}
          aria-label={label}
          className={cn(
            'group flex h-9 w-full min-w-0 items-center overflow-hidden no-underline [transition-property:background-color,color] duration-300',
            active ? SIDEBAR_ITEM_ACTIVE_CLASS : `text-[#6F655B] ${SIDEBAR_ITEM_HOVER_CLASS}`,
            SIDEBAR_ITEM_BASE_CLASS,
          )}
        >
          {' '}
          {/* Match the reference sidebars more literally: the visible row is just `w-full`, while the sidebar container owns the actual width animation. That keeps the control as one real row instead of splitting width/text/background across multiple nested elements, which was the source of the desynced behavior you kept seeing. */}
          <span className={SIDEBAR_ICON_SLOT_CLASS}>
            <Icon size={16} strokeWidth={2} className={cn('block shrink-0', href === '/a2ui-gallery' ? 'translate-y-[0.5px]' : '-translate-x-[0.5px] translate-y-[0.5px]')} />{' '}
            {/* The old `label === "a2ui preview"` guard was stale after the copy changed to `Gallery` / `Playground`, so both pinned icons were accidentally inheriting the same +1px right shift and looked visibly off-center in the collapsed rail. Keep the glyph-specific adjustment keyed to the stable route instead of the mutable label copy so later naming changes do not silently skew the icon lane again. */}
          </span>
          <span className={cn(SIDEBAR_PINNED_LABEL_CLASS, sidebarExpanded ? 'opacity-100' : 'opacity-0')}>
            {' '}
            {/* Like the reference implementations, the text stays in normal flex flow and only fades; it does not own width, x, or padding animation. The sidebar width transition reveals/hides available space, while the label itself simply crossfades. */}
            {label}
          </span>
        </Link>
      </div>
    </HoverCardTooltip>
  );
}

function MobileDrawerBody({
  currentCollection,
  exampleItems,
  pathname,
  onNavigate,
  onRequestClose,
}: {
  currentCollection: A2UIGalleryCollection;
  exampleItems: SidebarExampleItem[];
  pathname: string;
  onNavigate: (event: ReactMouseEvent<HTMLAnchorElement>, href: string) => void;
  onRequestClose: () => void;
}) {
  return (
    <div className="flex h-full min-h-0 flex-col p-2">
      <div className="flex h-12 shrink-0 items-center justify-between pl-3 pr-1">
        <DrawerTitle className="m-0 truncate text-sm font-semibold text-[#171411]">A2UI Workbench</DrawerTitle>
        <DrawerDescription className="sr-only">Mobile navigation for the Macaron A2UI Workbench.</DrawerDescription>
        <button type="button" aria-label="Close workbench navigation" onClick={onRequestClose} className="grid h-9 w-9 place-items-center rounded-sm text-[#6F655B] transition hover:bg-black/[0.04] active:scale-95">
          <X className="h-4 w-4" strokeWidth={2.2} />
        </button>
      </div>
      <nav className="mt-2 space-y-1" data-vaul-no-drag="">
        {WORKBENCH_SHORTCUTS.map(({ id, label, href, icon: Icon }) => (
          <MobileDrawerLink key={id} href={href} label={label} icon={Icon} active={isWorkbenchShortcutActive(href, pathname, currentCollection)} onNavigate={onNavigate} />
        ))}
      </nav>
      {isWorkbenchShortcutActive(GALLERY_ROUTE, pathname, currentCollection) || isWorkbenchShortcutActive(EXAMPLES_ROUTE, pathname, currentCollection) ? (
        <MobileDrawerExampleList currentCollection={currentCollection} exampleItems={exampleItems} pathname={pathname} onNavigate={onNavigate} />
      ) : null}
    </div>
  );
}

function MobileDrawerExampleList({
  currentCollection,
  exampleItems,
  pathname,
  onNavigate,
}: {
  currentCollection: A2UIGalleryCollection;
  exampleItems: SidebarExampleItem[];
  pathname: string;
  onNavigate: (event: ReactMouseEvent<HTMLAnchorElement>, href: string) => void;
}) {
  const { containerRef, topSentinelRef, bottomSentinelRef, showTopFade, showBottomFade } = useScrollFadeMask();
  return (
    <div className="mt-4 flex min-h-0 flex-1 flex-col overflow-hidden">
      <div className="px-3 text-[11px] font-medium uppercase tracking-[0.18em] text-[#9a8f84]">{currentCollection === 'examples' ? 'showcases' : 'components'}</div>
      <div className="relative mt-2 min-h-0 flex-1 overflow-hidden">
        <div ref={containerRef} className="h-full overflow-y-auto pr-1 no-scrollbar" style={getScrollFadeMaskStyle(showTopFade, showBottomFade)} data-vaul-no-drag="">
          <div ref={topSentinelRef} aria-hidden="true" className="h-px" />
          <nav className="space-y-1">
            {exampleItems.map(({ id, title }) => {
              const href = `/a2ui-gallery/${id}`;
              return <MobileDrawerTextLink key={id} href={href} label={title} active={pathname === href} onNavigate={onNavigate} />;
            })}
          </nav>
          <div ref={bottomSentinelRef} aria-hidden="true" className="h-px" />
        </div>
      </div>
    </div>
  );
}

function MobileDrawerLink({ href, label, icon: Icon, active, onNavigate }: { href: string; label: string; icon: typeof LayoutDashboard; active: boolean; onNavigate: (event: ReactMouseEvent<HTMLAnchorElement>, href: string) => void }) {
  return (
    <Link
      href={href}
      onClick={(event) => onNavigate(event, href)}
      aria-current={active ? 'page' : undefined}
      className={cn('flex h-10 min-w-0 items-center gap-2 rounded-sm px-3 text-sm font-medium no-underline transition', active ? SIDEBAR_ITEM_ACTIVE_CLASS : `text-[#6F655B] ${SIDEBAR_ITEM_HOVER_CLASS}`)}
    >
      <Icon size={16} strokeWidth={2} className="block shrink-0 translate-y-[0.5px]" />
      <span className="min-w-0 flex-1 truncate whitespace-nowrap">{label}</span>
    </Link>
  );
}

function MobileDrawerTextLink({ href, label, active, onNavigate }: { href: string; label: string; active: boolean; onNavigate: (event: ReactMouseEvent<HTMLAnchorElement>, href: string) => void }) {
  return (
    <Link
      href={href}
      onClick={(event) => onNavigate(event, href)}
      aria-current={active ? 'page' : undefined}
      className={cn('flex h-10 min-w-0 items-center rounded-sm px-3 text-sm font-medium no-underline transition', active ? SIDEBAR_ITEM_ACTIVE_CLASS : `text-[#6F655B] ${SIDEBAR_ITEM_HOVER_CLASS}`)}
    >
      <span className="min-w-0 flex-1 truncate whitespace-nowrap">{label}</span>
    </Link>
  );
}

function SidebarTextLink({ exampleId, href, label, active, hovered }: { exampleId: string; href: string; label: string; active: boolean; hovered: boolean }) {
  return (
    <Link
      href={href}
      data-a2ui-gallery-sidebar-example-id={exampleId}
      aria-current={active ? 'page' : undefined}
      aria-label={label}
      className={cn(
        'flex h-9 min-w-0 items-center gap-2 px-3 no-underline [transition-property:background-color,color] duration-300',
        SIDEBAR_ITEM_BASE_CLASS,
        active ? SIDEBAR_ITEM_ACTIVE_CLASS : hovered ? SIDEBAR_ITEM_CARD_HOVER_CLASS : `text-[#6F655B] ${SIDEBAR_ITEM_HOVER_CLASS}`,
      )}
    >
      {' '}
      {/* Reverse tracking still stays mostly declarative: the row only exposes a stable `data-a2ui-gallery-sidebar-example-id`, and the parent nav handles hover-driven scrolling in one place. That avoids sprinkling unreliable per-Link hover handlers across every row. */}
      <span className="min-w-0 flex-1 truncate whitespace-nowrap">{label}</span>
    </Link>
  );
}
